import React from "react";
import { Pagination } from "react-bootstrap";

interface PaginationControlsProps {
  currentPage: number; // 1-based page number
  totalItems: number; // Total count from API response.total
  itemsPerPage: number; // Page size (e.g., 50)
  onPageChange: (page: number) => void;
  maxPageButtons?: number; // Max visible page buttons (default: 5)
}

function PaginationControls({
  currentPage,
  totalItems,
  itemsPerPage,
  onPageChange,
  maxPageButtons = 5,
}: PaginationControlsProps) {
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Don't render if everything fits on one page
  if (totalItems <= itemsPerPage) {
    return null;
  }

  // Calculate the range of items being displayed
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  // Calculate which page buttons to show
  const getPageNumbers = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    const halfWindow = Math.floor(maxPageButtons / 2);

    let startPage = Math.max(1, currentPage - halfWindow);
    let endPage = Math.min(totalPages, currentPage + halfWindow);

    // Adjust if we're near the beginning or end
    if (currentPage <= halfWindow) {
      endPage = Math.min(maxPageButtons, totalPages);
    }
    if (currentPage > totalPages - halfWindow) {
      startPage = Math.max(1, totalPages - maxPageButtons + 1);
    }

    // Always show first page
    if (startPage > 1) {
      pages.push(1);
      if (startPage > 2) {
        pages.push("...");
      }
    }

    // Show the range of pages
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    // Always show last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push("...");
      }
      pages.push(totalPages);
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <div className="d-flex justify-content-between align-items-center mt-3">
      <div className="text-muted">
        Showing {startItem}-{endItem} of {totalItems}
      </div>
      <Pagination className="mb-0">
        <Pagination.Prev
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
        />
        {pageNumbers.map((page, index) => {
          if (page === "...") {
            return <Pagination.Ellipsis key={`ellipsis-${index}`} disabled />;
          }
          return (
            <Pagination.Item
              key={page}
              active={page === currentPage}
              onClick={() => onPageChange(page as number)}
            >
              {page}
            </Pagination.Item>
          );
        })}
        <Pagination.Next
          disabled={currentPage === totalPages}
          onClick={() => onPageChange(currentPage + 1)}
        />
      </Pagination>
    </div>
  );
}

export default PaginationControls;
